import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { searchApi } from '../../api/search';
import type { NavBarSearchResponseDto } from '../../api/types';
import { useSnackbar } from '../../context/SnackbarContext';

function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

interface GlobalSearchModalProps {
  open: boolean;
  onClose: () => void;
}

type SearchHit = {
  section: string;
  key: string;
  primary: string;
  secondary?: string;
  url: string;
};

function buildHits(data: NavBarSearchResponseDto | null): SearchHit[] {
  if (!data) return [];
  const out: SearchHit[] = [];
  for (const w of data.worksites) {
    out.push({
      section: 'Commesse',
      key: `w-${w.id}`,
      primary: w.name,
      url: `/app/worksites/${w.id}`,
    });
  }
  for (const c of data.customers) {
    out.push({
      section: 'Clienti',
      key: `c-${c.id}`,
      primary: c.name,
      url: `/app/customers/${c.id}`,
    });
  }
  for (const d of data.deliveryNotes) {
    out.push({
      section: 'DDT',
      key: `d-${d.id}`,
      primary: d.documentNumber ? `DDT ${d.documentNumber}` : 'DDT',
      secondary: d.supplierName,
      url: `/app/delivery-notes/${d.id}`,
    });
  }
  for (const r of data.reports) {
    const label = r.date ? `Rapportino ${r.date}` : 'Rapportino';
    out.push({
      section: 'Rapportini',
      key: `r-${r.id}`,
      primary: label,
      url: `/app/reports/${r.id}`,
    });
  }
  for (const s of data.suppliers) {
    out.push({
      section: 'Fornitori',
      key: `s-${s.id}`,
      primary: s.name,
      url: `/app/suppliers/${s.id}`,
    });
  }
  return out;
}

export function GlobalSearchModal({ open, onClose }: GlobalSearchModalProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const [q, setQ] = useState('');
  const debouncedQ = useDebounce(q, 300);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<NavBarSearchResponseDto | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const hits = useMemo(() => buildHits(data), [data]);

  useEffect(() => {
    if (!open) {
      setQ('');
      setData(null);
      setSelectedIndex(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = debouncedQ.trim();
    if (t.length < 2) {
      setData(null);
      setLoading(false);
      setSelectedIndex(0);
      return;
    }
    setLoading(true);
    searchApi
      .navBar(t, 5)
      .then((res) => {
        setData(res);
        setSelectedIndex(0);
      })
      .catch(() => showMessage('Ricerca non disponibile.', 'error'))
      .finally(() => setLoading(false));
  }, [open, debouncedQ, showMessage]);

  useEffect(() => {
    if (hits.length === 0) {
      setSelectedIndex(0);
      return;
    }
    setSelectedIndex((i) => Math.min(i, hits.length - 1));
  }, [hits.length]);

  const go = useCallback(
    (url: string) => {
      onClose();
      navigate(url);
    },
    [navigate, onClose],
  );

  const totalHits = hits.length;

  const handleKeyDownList = (e: React.KeyboardEvent) => {
    if (hits.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(hits.length - 1, i + 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Enter' && hits[selectedIndex]) {
      e.preventDefault();
      go(hits[selectedIndex].url);
    }
  };

  const handleSearchFieldKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (hits.length === 0) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (e.key === 'ArrowDown') {
        setSelectedIndex((i) => Math.min(hits.length - 1, i + 1));
      } else {
        setSelectedIndex((i) => Math.max(0, i - 1));
      }
      return;
    }
    if (e.key === 'Enter' && debouncedQ.trim().length >= 2 && hits[selectedIndex]) {
      e.preventDefault();
      go(hits[selectedIndex].url);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      scroll="paper"
      onKeyDown={handleKeyDownList}
    >
      <DialogTitle>Ricerca globale</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Cerca commesse, clienti, DDT…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={handleSearchFieldKeyDown}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />,
          }}
          sx={{ mb: 1 }}
        />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Minimo 2 caratteri. Fornitori: nome e P.IVA.
        </Typography>

        {loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && debouncedQ.trim().length >= 2 && data && totalHits === 0 && (
          <Typography color="text.secondary">Nessun risultato.</Typography>
        )}

        {!loading && totalHits > 0 && (
          <List dense disablePadding>
            {hits.map((hit, idx) => {
              const showHeader = idx === 0 || hits[idx - 1].section !== hit.section;
              return (
                <Box key={hit.key}>
                  {showHeader && (
                    <>
                      {idx > 0 && <Divider sx={{ my: 1 }} />}
                      <Typography variant="overline" color="text.secondary" sx={{ pl: 1, display: 'block', pt: 0.5 }}>
                        {hit.section}
                      </Typography>
                    </>
                  )}
                  <ListItemButton
                    selected={idx === selectedIndex}
                    onClick={() => go(hit.url)}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemText
                      primary={hit.primary}
                      secondary={
                        hit.secondary ? (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {hit.secondary}
                          </Typography>
                        ) : undefined
                      }
                    />
                  </ListItemButton>
                </Box>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
